import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Loader2, Upload, User } from "lucide-react";
import { api } from "../../api";
import { DESK } from "../../constants/testIds";
import { FormField } from "../common/FormField";
import { Label } from "../ui/label";
import { toast } from "../ui/sonner";
import { downscaleImage } from "../../lib/image";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

const GENDERS = [["male", "Male"], ["female", "Female"], ["other", "Other"]];
const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
const RELATIONSHIPS = ["Spouse", "Parent", "Sibling", "Friend", "Other"];
const OTHER_DOC_TYPES = ["Passport", "Voter ID", "Driving License", "Other"];

// Shared "personal details" capture — used by both the Walk-in "New member" flow (step 1) and
// the Add-family "brand-new person" branch. Mobile itself is captured separately by each caller
// since it's handled differently in each context (mandatory top-level field on walk-in vs. the
// already-searched number on the family modal). Only Emergency contact name + mobile are
// required here — everything else, including the whole KYC block, is optional.
export function MemberPersonalFields({ form, onChange }) {
  const [photoPreview, setPhotoPreview] = useState(null);

  const uploadPhotoMutation = useMutation({
    mutationFn: (file) => {
      const body = new FormData();
      body.append("file", file);
      return api.post("/members/photo", body);
    },
    onSuccess: (res) => onChange("photoKey", res.data.photo_key),
    onError: () => toast.error("Couldn't upload that photo — try a different file."),
  });

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoPreview(URL.createObjectURL(file));
    // Shrink before the size check, so a straight-off-the-phone photo passes instead of being
    // rejected for pixels no avatar will ever show.
    const upload = await downscaleImage(file);
    if (upload.size > 10 * 1024 * 1024) {
      toast.error("Photo must be under 10 MB.");
      setPhotoPreview(null);
      return;
    }
    uploadPhotoMutation.mutate(upload);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <div
          className="rounded-full overflow-hidden flex items-center justify-center shrink-0"
          style={{ width: 56, height: 56, background: "#F4F3EF", border: "1px solid #EBEAE5" }}
          data-testid={DESK.memberPhotoPreview}
        >
          {photoPreview ? <img src={photoPreview} alt="" className="w-full h-full object-cover" /> : <User size={22} style={{ color: "#68686E" }} />}
        </div>
        <label className="cursor-pointer">
          <span
            className="text-sm font-medium inline-flex items-center gap-1.5 rounded-full px-3 py-1.5"
            style={{ border: "1px solid #EBEAE5", color: "#141416" }}
          >
            {uploadPhotoMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploadPhotoMutation.isPending ? "Uploading…" : "Upload photo"}
          </span>
          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid={DESK.memberPhotoInput} onChange={handlePhotoChange} />
        </label>
        <span className="text-xs" style={{ color: "#68686E" }}>JPG, PNG or WEBP</span>
      </div>

      <FormField label="Email" type="email" testId={DESK.walkinEmailInput} value={form.email} onChange={(v) => onChange("email", v)} helperText="Optional" />
      <FormField label="WhatsApp number" type="tel" testId={DESK.memberWhatsappInput} value={form.whatsapp} onChange={(v) => onChange("whatsapp", v)} helperText="Optional — defaults to mobile if left blank" />

      <div className="grid grid-cols-2 gap-3">
        <FormField label="Date of birth" type="date" testId={DESK.memberDobInput} value={form.dob} onChange={(v) => onChange("dob", v)} />
        <div className="space-y-1.5">
          <Label>Blood group</Label>
          <Select onValueChange={(v) => onChange("bloodGroup", v)} value={form.bloodGroup || undefined}>
            <SelectTrigger data-testid={DESK.memberBloodGroupSelect}><SelectValue placeholder="Optional" /></SelectTrigger>
            <SelectContent>
              {BLOOD_GROUPS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Gender <span className="text-xs font-normal" style={{ color: "#68686E" }}>(optional)</span></Label>
        <div className="flex gap-2">
          {GENDERS.map(([value, label]) => (
            <Pill key={value} testId={DESK.memberGenderPill(value)} active={form.gender === value} onClick={() => onChange("gender", form.gender === value ? "" : value)}>
              {label}
            </Pill>
          ))}
        </div>
      </div>

      <FormField label="Alternate mobile" type="tel" testId={DESK.memberAlternateMobileInput} value={form.alternateMobile} onChange={(v) => onChange("alternateMobile", v)} helperText="Optional" />
      <FormField label="Occupation" testId={DESK.memberOccupationInput} value={form.occupation} onChange={(v) => onChange("occupation", v)} helperText="Optional" />

      <div className="rounded-[10px] p-4" style={{ background: "#F4F3EF" }}>
        <p className="text-sm font-semibold mb-0.5" style={{ color: "#141416" }}>Emergency contact</p>
        <p className="text-xs mb-3" style={{ color: "#68686E" }}>Required — used only in case of medical incidents.</p>
        <div className="space-y-3">
          <FormField label="Contact name" required testId={DESK.emergencyNameInput} value={form.emergencyName} onChange={(v) => onChange("emergencyName", v)} />
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Relationship <span className="text-xs font-normal" style={{ color: "#68686E" }}>(optional)</span></Label>
              <Select onValueChange={(v) => onChange("emergencyRelationship", v)} value={form.emergencyRelationship || undefined}>
                <SelectTrigger data-testid={DESK.emergencyRelationshipSelect}><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  {RELATIONSHIPS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <FormField label="Contact mobile" required type="tel" testId={DESK.emergencyMobileInput} value={form.emergencyMobile} onChange={(v) => onChange("emergencyMobile", v)} />
          </div>
          <FormField label="Alt mobile" type="tel" testId={DESK.emergencyAltMobileInput} value={form.emergencyAltMobile} onChange={(v) => onChange("emergencyAltMobile", v)} helperText="Optional" />
        </div>
      </div>

      <div className="rounded-[10px] p-4" style={{ border: "1px solid #EBEAE5" }}>
        <p className="text-sm font-semibold mb-3" style={{ color: "#141416" }}>KYC <span className="text-xs font-normal" style={{ color: "#68686E" }}>(optional)</span></p>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Aadhaar number" testId={DESK.kycAadhaarInput} value={form.aadhaar} onChange={(v) => onChange("aadhaar", v)} />
            <FormField label="PAN number" testId={DESK.kycPanInput} value={form.pan} onChange={(v) => onChange("pan", v)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Other document type</Label>
              <Select onValueChange={(v) => onChange("otherDocType", v)} value={form.otherDocType || undefined}>
                <SelectTrigger data-testid={DESK.kycOtherDocTypeSelect}><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  {OTHER_DOC_TYPES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <FormField label="Document number" testId={DESK.kycOtherDocNumberInput} value={form.otherDocNumber} onChange={(v) => onChange("otherDocNumber", v)} />
          </div>
        </div>
      </div>
    </div>
  );
}

export function Pill({ testId, active, onClick, children }) {
  return (
    <button
      type="button" data-testid={testId} onClick={onClick}
      className="text-sm font-medium rounded-full px-3.5 py-1.5 transition-colors"
      style={active ? { background: "#D6202B", color: "#fff", border: "1px solid #D6202B" } : { background: "#fff", color: "#141416", border: "1px solid #EBEAE5" }}
    >
      {children}
    </button>
  );
}

export const EMPTY_PERSONAL_FORM = {
  email: "", whatsapp: "", dob: "", gender: "", bloodGroup: "", alternateMobile: "", occupation: "", photoKey: "",
  emergencyName: "", emergencyRelationship: "", emergencyMobile: "", emergencyAltMobile: "",
  aadhaar: "", pan: "", otherDocType: "", otherDocNumber: "",
};

export function isPersonalFormValid(form) {
  return Boolean(form.emergencyName?.trim()) && Boolean(form.emergencyMobile?.trim());
}

// Maps the shared personal-form state onto the exact `MemberCreateRequest` field names the
// backend expects (see models_commercial.py) — kept here, next to the form itself, so the two
// never drift out of sync.
export function buildPersonalPayload(form) {
  return {
    email: form.email || null,
    whatsapp: form.whatsapp || null,
    dob: form.dob || null,
    gender: form.gender || null,
    blood_group: form.bloodGroup || null,
    alternate_mobile: form.alternateMobile || null,
    occupation: form.occupation || null,
    photo_key: form.photoKey || null,
    emergency_contact: form.emergencyName && form.emergencyMobile ? {
      name: form.emergencyName, relationship: form.emergencyRelationship || null,
      mobile: form.emergencyMobile, alt_mobile: form.emergencyAltMobile || null,
    } : null,
    kyc: (form.aadhaar || form.pan || form.otherDocType || form.otherDocNumber) ? {
      aadhaar_number: form.aadhaar || null, pan_number: form.pan || null,
      other_document_type: form.otherDocType || null, other_document_number: form.otherDocNumber || null,
    } : null,
  };
}
